import { API_BASE_URL } from "../config/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { checkRealOnlineStatus } from "../utils/offlineAdapter";

const LIST_REQUEST_TIMEOUT_MS = 25000;

const isSameDay = (value) => {
  if (!value) return false;

  const parsed = parseComplaintDate(value);
  if (Number.isNaN(parsed.getTime())) return false;

  const today = new Date();
  return (
    parsed.getFullYear() === today.getFullYear() &&
    parsed.getMonth() === today.getMonth() &&
    parsed.getDate() === today.getDate()
  );
};

const parseComplaintDate = (value) => {
  if (!value) return new Date("");

  const normalized = String(value || "").trim();
  const dateMatch = normalized.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (dateMatch) {
    return new Date(Number(dateMatch[3]), Number(dateMatch[2]) - 1, Number(dateMatch[1]));
  }

  return new Date(value);
};

const formatComplaintDate = (value) => {
  if (!value) return "-";

  const parsed = parseComplaintDate(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const getAssignedDate = (complaint = {}) => {
  if (complaint.visitDate) return complaint.visitDate;
  if (complaint.engineerAssignedAt) return complaint.engineerAssignedAt;

  const timeline = Array.isArray(complaint.statusTimeline) ? complaint.statusTimeline : [];
  const assignmentEntry = [...timeline]
    .reverse()
    .find((entry) => String(entry.label || "").toLowerCase().includes("engineer assigned"));

  return assignmentEntry?.at || "";
};

const toDateInputValue = (value) => {
  if (!value) return "";

  const parsed = parseComplaintDate(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isCompletedStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "completed";
};

const isPendingFilterStatus = (value = "") => !isCompletedStatus(value);

const normalizeComplaintStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "created") return "assigned";
  if (normalized === "closed") return "completed";
  if (["revisit", "re visit", "re-visit"].includes(normalized)) return "re-visit";
  if (normalized === "customer waiting for order") return "need to provide quote";
  if (["pending", "assigned", "in progress", "re-visit", "completed", "need to provide quote", "other"].includes(normalized)) {
    return normalized;
  }
  return "pending";
};

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const isNewerReport = (candidate, current) =>
  new Date(candidate?.updatedAt || candidate?.createdAt || 0) >
  new Date(current?.updatedAt || current?.createdAt || 0);

const getComplaintEngineerValue = (complaint) =>
  complaint.engineerAssignedId || complaint.engineerAssignedEmail || complaint.engineerAssigned || "";

const engineerOptionValue = (engineer) =>
  engineer?.engineerId || engineer?.email || engineer?.name || "";

const normalizeServiceStatus = (value = "") => {
  const normalized = String(value || "").trim();
  return normalized || "Assigned";
};

const isAbbWarrantyComplaint = (cmp) => {
  if (!cmp) return false;
  if (cmp.productType === "ABB Warranty") return true;
  if (cmp.siteAdvice && String(cmp.siteAdvice).includes("ABB Warranty Complaint created from Voucher")) return true;
  const pType = String(cmp.productType || "").toLowerCase();
  const issue = String(cmp.issue || "").toLowerCase();
  if (pType.includes("abb") && pType.includes("warranty")) return true;
  if (issue.includes("abb") && issue.includes("warranty")) return true;
  return false;
};

const complaintStatusFilters = [
  { value: "pending", label: "Pending" },
  { value: "assigned", label: "Assigned" },
  { value: "in progress", label: "In Progress" },
  { value: "re-visit", label: "Re-Visit" },
  { value: "need to provide quote", label: "Need to provide quote" },
  { value: "other", label: "Other" },
  { value: "completed", label: "Completed" }
];

const formatAssignedEngineers = (service = {}) => {
  const items = Array.isArray(service.assignedEngineers) ? service.assignedEngineers : [];
  if (items.length === 0) return "Unassigned";

  return items
    .map((item) => item.engineerName || item.engineerId || item.engineerEmail)
    .filter(Boolean)
    .join(", ");
};

const formatAddress = (address) => {
  if (!address) return "";
  if (typeof address === "string") return address.trim();

  return [address.street, address.area, address.city, address.state, address.pincode]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
};

export default function Complaints() {
  const role = localStorage.getItem("role") || "";
  const canManageComplaints = role === "admin";
  const columnCount = canManageComplaints ? 9 : 8;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [travelByComplaintId, setTravelByComplaintId] = useState({});
  const [reportByComplaintId, setReportByComplaintId] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [serverUnavailable, setServerUnavailable] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [assigningId, setAssigningId] = useState("");
  const [engineerSelection, setEngineerSelection] = useState({});
  const [visibleCount, setVisibleCount] = useState(20);
  const [dynamicTeams, setDynamicTeams] = useState([]);
  const [openActionDropdownId, setOpenActionDropdownId] = useState(null);

  useEffect(() => {
    setVisibleCount(20);
  }, [search, searchParams]);

  useEffect(() => {
    const handleClose = () => setOpenActionDropdownId(null);
    if (openActionDropdownId) {
      document.addEventListener("click", handleClose);
      return () => document.removeEventListener("click", handleClose);
    }
  }, [openActionDropdownId]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    axios.get(API_BASE_URL + "/api/admin-settings/system-settings", { headers })
      .then(res => {
        if (res.data && Array.isArray(res.data.teams)) {
          setDynamicTeams(res.data.teams);
        } else {
          setDynamicTeams([
            { id: "team_1", name: "Team 1" },
            { id: "team_2", name: "Team 2" },
            { id: "team_3", name: "Team 3" }
          ]);
        }
      })
      .catch(err => {
        console.error("Error loading system settings teams:", err);
        setDynamicTeams([
          { id: "team_1", name: "Team 1" },
          { id: "team_2", name: "Team 2" },
          { id: "team_3", name: "Team 3" }
        ]);
      });
  }, []);

  const formatAssignedEngineersForComplaint = (complaint) => {
    const items = Array.isArray(complaint.assignedEngineers) ? complaint.assignedEngineers : [];
    if (items.length === 0) {
      return getComplaintEngineerLabel(complaint);
    }

    return items
      .map((item) => {
        const name = item.engineerName || item.engineerId || item.engineerEmail;
        const matchedTeam = dynamicTeams.find(t => t.id === item.team || t.name === item.team);
        const teamName = matchedTeam ? matchedTeam.name : (item.team === "team_1" ? "Team 1" : item.team === "team_2" ? "Team 2" : item.team === "team_3" ? "Team 3" : item.team);
        return name + (teamName ? ` (${teamName})` : "");
      })
      .filter(Boolean)
      .join(", ");
  };

  const statusFilter = String(searchParams.get("status") || "").toLowerCase();
  const typeFilter = String(searchParams.get("type") || "").toLowerCase();
  const todayOnly = searchParams.get("today") === "1";
  const complaintFilter = String(searchParams.get("complaintId") || "").trim();
  const dateFilter = String(searchParams.get("date") || "").trim();

  const fetchData = useCallback(async () => {
    const requestConfig = { timeout: LIST_REQUEST_TIMEOUT_MS };
    const onlineStatusPromise = checkRealOnlineStatus();

    try {
      setLoading(true);
      setLoadError("");
      setServerUnavailable(false);

      const [
        complaintsResult,
        servicesResult,
        customersResult,
        visitsResult,
        engineersResult,
        travelResult
      ] = await Promise.allSettled([
        axios.get(API_BASE_URL + "/api/complaints", requestConfig),
        axios.get(API_BASE_URL + "/api/general-services", requestConfig),
        axios.get(API_BASE_URL + "/api/customers", requestConfig),
        axios.get(API_BASE_URL + "/api/visits", {
          ...requestConfig,
          params: { excludePdf: 1 }
        }),
        axios.get(API_BASE_URL + "/api/engineers", requestConfig),
        axios.get(API_BASE_URL + "/api/travel", requestConfig)
      ]);

      const complaintData =
        complaintsResult.status === "fulfilled" && Array.isArray(complaintsResult.value.data)
          ? complaintsResult.value.data
          : [];
      const serviceData =
        servicesResult.status === "fulfilled" && Array.isArray(servicesResult.value.data)
          ? servicesResult.value.data
          : [];
      const customerData =
        customersResult.status === "fulfilled" && Array.isArray(customersResult.value.data)
          ? customersResult.value.data
          : [];
      const visitData =
        visitsResult.status === "fulfilled" && Array.isArray(visitsResult.value.data)
          ? visitsResult.value.data
          : [];
      const engineerData =
        engineersResult.status === "fulfilled" && Array.isArray(engineersResult.value.data)
          ? engineersResult.value.data
          : [];
      const travelData =
        travelResult.status === "fulfilled" && Array.isArray(travelResult.value.data)
          ? travelResult.value.data
          : [];

      const nextReportMap = visitData.reduce((acc, report) => {
        [report.complaintId, report.complaintRefId, report._id, report.visitId]
          .filter(Boolean)
          .forEach((key) => {
            const normalizedKey = String(key).trim();
            if (!acc[normalizedKey] || isNewerReport(report, acc[normalizedKey])) {
              acc[normalizedKey] = report;
            }
          });
        return acc;
      }, {});

      const nextTravelMap = travelData.reduce((acc, travel) => {
        const keys = [travel.complaintId, travel.complaintRefId, travel._id].filter(Boolean).map((k) => String(k).trim());

        keys.forEach((key) => {
          const existing = acc[key];
          if (!existing || new Date(travel.createdAt) > new Date(existing.createdAt)) {
            acc[key] = travel;
          }
        });

        return acc;
      }, {});

      setComplaints(complaintData);
      setServices(serviceData);
      setCustomers(customerData);
      setEngineers(engineerData);
      setTravelByComplaintId(nextTravelMap);
      setReportByComplaintId(nextReportMap);
      setEngineerSelection(
        complaintData.reduce((acc, complaint) => {
          acc[complaint._id] = getComplaintEngineerValue(complaint);
          return acc;
        }, {})
      );

      const serverIsOnline = await onlineStatusPromise;
      setServerUnavailable(!serverIsOnline);

      if (complaintsResult.status === "rejected" && servicesResult.status === "rejected") {
        setLoadError(
          serverIsOnline
            ? "Complaints could not be loaded. Please try again."
            : "The Genmech backend is not running. Start it on port 3000, then retry."
        );
      }
    } catch (error) {
      console.error("Error loading complaints:", error);
      setComplaints([]);
      setServices([]);
      setCustomers([]);
      setEngineers([]);
      setTravelByComplaintId({});
      setReportByComplaintId({});
      setEngineerSelection({});
      setLoadError("Complaints could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customerById = useMemo(
    () => new Map(customers.map((customer) => [customer._id, customer])),
    [customers]
  );
  const customerMap = useMemo(
    () => new Map(customers.map((customer) => [customer._id, customer.name])),
    [customers]
  );
  const engineerByLookup = useMemo(() => {
    const map = new Map();
    engineers.forEach((engineer) => {
      [engineer._id, engineer.engineerId, engineer.email, engineer.name]
        .filter(Boolean)
        .forEach((key) => map.set(String(key), engineer));
    });
    return map;
  }, [engineers]);
  const getComplaintEngineerLabel = (complaint) => {
    const lookup = getComplaintEngineerValue(complaint);
    return complaint.engineerAssigned || engineerByLookup.get(String(lookup))?.name || "Unassigned";
  };

  useEffect(() => {
    setSearch(complaintFilter || "");
  }, [complaintFilter]);

  const combinedRecords = useMemo(() => {
    const complaintRecords = complaints.map((complaint) => ({
      ...complaint,
      recordType: "complaint",
      isAbbWarranty: isAbbWarrantyComplaint(complaint),
      primaryId: complaint.complaintId || complaint._id,
      recordDate: getAssignedDate(complaint),
      customerLabel: customerMap.get(complaint.customerId) || complaint.company || "-",
      addressLabel: complaint.address || formatAddress(customerById.get(complaint.customerId)?.address) || formatAddress(customerById.get(complaint.customerId)?.billingAddress) || "-",
      engineerLabel: complaint.engineerAssigned || engineerByLookup.get(String(getComplaintEngineerValue(complaint)))?.name || "Unassigned",
      normalizedStatus: normalizeComplaintStatus(complaint.status)
    }));

    const serviceRecords = services.map((service) => ({
      ...service,
      recordType: "service",
      isAbbWarranty: false,
      primaryId: service.serviceId || service._id,
      recordDate: service.serviceDate || service.createdAt || "",
      customerLabel: service.customerName || service.company || "-",
      addressLabel: service.address || "-",
      engineerLabel: formatAssignedEngineers(service),
      normalizedStatus: normalizeServiceStatus(service.status)
    }));

    return [...complaintRecords, ...serviceRecords].sort((left, right) => {
      const leftDate = parseComplaintDate(left.recordDate || left.updatedAt || left.createdAt || "");
      const rightDate = parseComplaintDate(right.recordDate || right.updatedAt || right.createdAt || "");
      return rightDate - leftDate;
    });
  }, [complaints, customerById, customerMap, engineerByLookup, services]);

  const updateFilters = (updates = {}) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        nextParams.delete(key);
      } else {
        nextParams.set(key, value);
      }
    });

    setSearchParams(nextParams);
  };

  const filteredComplaints = combinedRecords.filter((record) => {
    const customer = record.customerLabel || "-";
    const engineer = record.recordType === "complaint"
      ? (engineerSelection[record._id] ?? getComplaintEngineerValue(record) ?? "-")
      : record.engineerLabel || "-";
    const complaintStatus = record.normalizedStatus;
    const assignedDate = record.recordDate;
    const complaintDate = toDateInputValue(assignedDate);
    const matchesComplaintFilter = complaintFilter
      ? String(record._id) === complaintFilter || String(record.primaryId) === complaintFilter
      : true;

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "pending"
        ? record.recordType === "service"
          ? complaintStatus !== "Reports Merged"
          : isPendingFilterStatus(complaintStatus)
        : statusFilter === "completed"
          ? record.recordType === "service"
            ? complaintStatus === "Reports Merged"
            : isCompletedStatus(complaintStatus)
          : String(complaintStatus).toLowerCase() === statusFilter);

    const matchesToday = !todayOnly || isSameDay(assignedDate);
    const matchesDate = !dateFilter || complaintDate === dateFilter;
    const matchesType = !typeFilter || (typeFilter === "abb_warranty" ? record.isAbbWarranty : typeFilter === "standard" ? !record.isAbbWarranty : true);

    const matchesSearch = [
      record.primaryId,
      record.recordType,
      record.isAbbWarranty ? "abb warranty" : "",
      customer,
      record.addressLabel,
      engineer,
      complaintStatus,
      formatComplaintDate(assignedDate)
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase());

    return matchesComplaintFilter && matchesStatus && matchesToday && matchesDate && matchesType && matchesSearch;
  });

  const visibleComplaints = useMemo(
    () => filteredComplaints.slice(0, visibleCount),
    [filteredComplaints, visibleCount]
  );

  const handleDeleteComplaint = async (complaint) => {
    const confirmed = window.confirm(`Are you sure you want to delete complaint ${complaint.complaintId}?`);

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(complaint._id);
      await axios.delete(API_BASE_URL + `/api/complaints/${complaint._id}`);
      setComplaints((prev) => prev.filter((item) => item._id !== complaint._id));
    } catch (error) {
      console.error("Error deleting complaint:", error);
      alert(error.response?.data?.message || "Failed to delete complaint");
    } finally {
      setDeletingId("");
    }
  };

  const updateStatus = async (id, status) => {
    if (status === "re-visit") {
      const complaint = complaints.find((item) => item._id === id);
      if (!complaint) return;

      try {
        const res = await axios.get(API_BASE_URL + `/api/complaints/${id}/revisit-id`);
        navigate("/create-complaint", {
          state: {
            prefill: {
              ...complaint,
              complaintId: res.data?.complaintId || `${complaint.complaintId}-1`,
              revisitOf: res.data?.revisitOf || complaint._id,
              originalComplaintId: res.data?.originalComplaintId || complaint.complaintId,
              engineerAssigned: "",
              siteAdvice: ""
            }
          }
        });
      } catch (error) {
        alert(error.response?.data?.message || "Failed to start revisit complaint");
      }
      return;
    }

    try {
      const res = await axios.put(API_BASE_URL + `/api/complaints/${id}`, { status });
      setComplaints((current) =>
        current.map((item) => (item._id === id ? res.data : item))
      );
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update complaint status");
    }
  };

  const updateEngineer = async (complaint) => {
    const engineer = String(engineerSelection[complaint._id] || "").trim();

    try {
      setAssigningId(complaint._id);
      const res = await axios.put(API_BASE_URL + `/api/complaints/assign/${complaint._id}`, {
        engineer
      });

      setComplaints((current) =>
        current.map((item) => (item._id === complaint._id ? res.data : item))
      );
      setEngineerSelection((current) => ({
        ...current,
        [complaint._id]: getComplaintEngineerValue(res.data)
      }));
    } catch (error) {
      alert(error.response?.data?.message || "Failed to reassign engineer");
    } finally {
      setAssigningId("");
    }
  };

  return (
    <div className="mobile-dense-page space-y-4 lg:space-y-6">
      <div className="flex flex-col gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 lg:hidden">Service Desk</p>
          <h1 className="text-2xl font-bold text-slate-950">Complaints & Services</h1>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
          <div className="flex flex-wrap items-center gap-2.5 flex-1">
            <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">
                search
              </span>
              <input
                type="text"
                placeholder="Search complaints / ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-9 pr-3 py-2 text-xs sm:text-sm font-medium focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter Dropdown */}
            <div className="min-w-[150px]">
              <select
                value={statusFilter}
                onChange={(e) => updateFilters({ status: e.target.value, today: "" })}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs sm:text-sm font-semibold text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                {complaintStatusFilters.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Type / Category Dropdown */}
            <div className="min-w-[150px]">
              <select
                value={typeFilter}
                onChange={(e) => updateFilters({ type: e.target.value })}
                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs sm:text-sm font-semibold text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                <option value="abb_warranty">ABB Warranty</option>
                <option value="standard">Standard Services</option>
              </select>
            </div>

            {/* Date Filter */}
            <div>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => updateFilters({ date: e.target.value, today: "" })}
                className="rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs sm:text-sm font-medium text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Today Button */}
            <button
              type="button"
              onClick={() => updateFilters({ today: todayOnly ? "" : "1", date: "" })}
              className={`rounded-xl px-3 py-2 text-xs sm:text-sm font-bold border transition ${
                todayOnly
                  ? "bg-violet-600 text-white border-violet-600 shadow-2xs"
                  : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              Today
            </button>

            {/* Clear / Reset Filters */}
            {(statusFilter || typeFilter || dateFilter || todayOnly || search) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setSearchParams({});
                }}
                className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-2 text-xs sm:text-sm font-bold border border-slate-200 transition flex items-center gap-1"
                title="Clear all filters"
              >
                <span className="material-symbols-outlined text-sm">clear_all</span>
                Reset
              </button>
            )}
          </div>

          {canManageComplaints && (
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/create-complaint?type=abb_warranty">
                <button className="rounded-xl bg-teal-600 hover:bg-teal-700 px-3.5 py-2 text-xs sm:text-sm font-bold text-white transition flex items-center gap-1.5 shadow-2xs">
                  <span className="material-symbols-outlined text-base">verified_user</span>
                  + ABB Warranty
                </button>
              </Link>
              <Link to="/create-complaint">
                <button className="rounded-xl bg-blue-600 hover:bg-blue-700 px-3.5 py-2 text-xs sm:text-sm font-bold text-white transition flex items-center gap-1.5 shadow-2xs">
                  <span className="material-symbols-outlined text-base">add</span>
                  + Create Service
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {serverUnavailable && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
          <span>The local Genmech backend is unavailable. Showing any data saved on this computer.</span>
          <button type="button" onClick={fetchData} className="w-fit rounded-lg border border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-900 hover:bg-amber-100">
            Retry
          </button>
        </div>
      )}

      {!serverUnavailable && loadError && (
        <div className="flex flex-col gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 sm:flex-row sm:items-center sm:justify-between">
          <span>{loadError}</span>
          <button type="button" onClick={fetchData} className="w-fit rounded-lg border border-rose-300 bg-white px-3 py-1.5 font-semibold text-rose-800 hover:bg-rose-100">
            Retry
          </button>
        </div>
      )}

      <div className="space-y-3 lg:hidden">
        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 shadow-sm">
            Loading complaints...
          </div>
        )}

        {!loading && filteredComplaints.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 shadow-sm">
            No complaints found
          </div>
        )}

        {!loading && visibleComplaints.map((complaint) => {
          const isServiceRecord = complaint.recordType === "service";
          const linkedReport =
            !isServiceRecord
              ? reportByComplaintId[String(complaint._id)] ||
                reportByComplaintId[String(complaint.complaintId || "").trim()] ||
                reportByComplaintId[String(complaint.primaryId || "").trim()]
              : null;
          const linkedTravel =
            !isServiceRecord
              ? travelByComplaintId[String(complaint._id)] ||
                travelByComplaintId[String(complaint.complaintId || "").trim()] ||
                travelByComplaintId[String(complaint.primaryId || "").trim()]
              : null;
          const travelStarted = Boolean(
            !isServiceRecord &&
            linkedTravel &&
            (
              linkedTravel.status !== "Not Started" ||
              linkedTravel.journey?.startTime
            )
          );
          const canVerify = Boolean(linkedReport?._id);
          const approvalStatus = normalizeApprovalStatus(linkedReport?.approvalStatus);
          const normalizedStatus = isServiceRecord
            ? normalizeServiceStatus(complaint.status)
            : normalizeComplaintStatus(complaint.status);

          return (
            <article key={complaint._id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="break-words text-base font-bold text-slate-950">{complaint.primaryId}</h2>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      isServiceRecord
                        ? "bg-violet-100 text-violet-700"
                        : complaint.isAbbWarranty
                          ? "bg-teal-100 text-teal-800"
                          : "bg-sky-100 text-sky-700"
                    }`}>
                      {isServiceRecord ? "Service" : complaint.isAbbWarranty ? "ABB Warranty" : "Complaint"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{isServiceRecord ? "Service Date" : "Assigned Date"}</p>
                  <p className="text-sm font-medium text-slate-800">{formatComplaintDate(complaint.recordDate)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-bold uppercase ${
                  normalizedStatus === "completed" || normalizedStatus === "Reports Merged"
                    ? "bg-emerald-100 text-emerald-700"
                  : normalizedStatus === "in progress"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-amber-100 text-amber-700"
                }`}>
                  {normalizedStatus}
                </span>
              </div>

              <div className="mt-4 grid gap-3 text-sm">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Customer Name</p>
                  <p className="mt-1 font-medium text-slate-800">{complaint.customerLabel}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Address</p>
                  <p className="mt-1 break-words font-medium leading-6 text-slate-800">{complaint.addressLabel}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {isServiceRecord ? "Engineers" : "Engineer"}
                  </p>
                  <p className="mt-1 break-words font-medium leading-6 text-slate-800">
                    {isServiceRecord ? complaint.engineerLabel : formatAssignedEngineersForComplaint(complaint)}
                  </p>
                </div>
              </div>

              {!isServiceRecord && linkedReport && (
                <p className={`mt-3 text-xs font-semibold ${
                  approvalStatus === "Approved"
                    ? "text-emerald-700"
                    : approvalStatus === "Changes Requested"
                    ? "text-rose-700"
                    : "text-amber-700"
                }`}>
                  {approvalStatus}
                </p>
              )}

              {canManageComplaints && !isServiceRecord && (
                <div className="mt-4 grid gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Change Engineer</p>
                  <select
                    value={engineerSelection[complaint._id] ?? getComplaintEngineerValue(complaint) ?? ""}
                    onChange={(e) =>
                      setEngineerSelection((current) => ({
                        ...current,
                        [complaint._id]: e.target.value
                      }))
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                    disabled={travelStarted || assigningId === complaint._id}
                  >
                    <option value="">Unassigned</option>
                    {engineers.map((engineer) => (
                      <option key={engineer._id} value={engineerOptionValue(engineer)}>
                        {engineer.engineerId ? `${engineer.name} (${engineer.engineerId})` : engineer.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={normalizedStatus}
                    onChange={(e) => updateStatus(complaint._id, e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="assigned">Assigned</option>
                    <option value="in progress">In Progress</option>
                    <option value="re-visit">Re-Visit</option>
                    <option value="need to provide quote">Need to provide quote</option>
                    <option value="other">Other</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              )}


              <div className="mt-4 grid grid-cols-2 gap-2">
                {canManageComplaints && !isServiceRecord && (
                  <button
                    type="button"
                    onClick={() => updateEngineer(complaint)}
                    disabled={travelStarted || assigningId === complaint._id}
                    className="rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {assigningId === complaint._id ? "Saving..." : "Assign"}
                  </button>
                )}
                {isServiceRecord ? (
                  <>
                    <button
                      type="button"
                      onClick={() => navigate(`/create-complaint?type=service&serviceId=${complaint._id}`)}
                      className="rounded-xl bg-amber-500 px-3 py-2.5 text-sm font-semibold text-white"
                    >
                      Edit Service
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/general-services")}
                      className="rounded-xl bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white"
                    >
                      Manage Reports
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => canVerify && navigate(canManageComplaints ? `/reports/${linkedReport._id}` : `/accounts/reports/${linkedReport._id}`)}
                      disabled={!canVerify}
                      className={`rounded-xl px-3 py-2.5 text-sm font-semibold ${
                        canVerify
                          ? "bg-emerald-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {canVerify ? "View More" : "No Report"}
                    </button>
                    {canManageComplaints && (
                      <div className="relative inline-block text-left" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setOpenActionDropdownId(openActionDropdownId === complaint._id ? null : complaint._id)}
                          className="w-full flex items-center justify-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
                        >
                          <span>Actions</span>
                          <span className="material-symbols-outlined text-sm">arrow_drop_down</span>
                        </button>

                        {openActionDropdownId === complaint._id && (
                          <div className="absolute right-0 bottom-full mb-1 w-44 rounded-xl bg-white shadow-lg border border-slate-200 py-1 z-30 focus:outline-none divide-y divide-slate-100">
                            <button
                              type="button"
                              onClick={() => {
                                setOpenActionDropdownId(null);
                                navigate(isServiceRecord ? `/create-complaint?type=service&serviceId=${complaint._id}` : `/complaints/${complaint._id}/edit`);
                              }}
                              className="w-full text-left px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-2 transition"
                            >
                              <span className="material-symbols-outlined text-sm text-amber-500">edit</span>
                              {isServiceRecord ? "Edit Service" : "Edit Complaint"}
                            </button>
                            {!isServiceRecord && (
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenActionDropdownId(null);
                                  handleDeleteComplaint(complaint);
                                }}
                                disabled={deletingId === complaint._id}
                                className="w-full text-left px-3.5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2 transition"
                              >
                                <span className="material-symbols-outlined text-sm text-rose-600">delete</span>
                                {deletingId === complaint._id ? "Deleting..." : "Delete Complaint"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="hidden overflow-hidden rounded-2xl bg-white shadow lg:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1020px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Customer Name</th>
                <th className="p-3 text-left">Address</th>
                <th className="p-3 text-left">Engineer</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Verify</th>
                {canManageComplaints && <th className="p-3 text-left">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columnCount} className="p-4 text-center text-gray-500">
                    Loading complaints...
                  </td>
                </tr>
              )}

              {!loading && !loadError && filteredComplaints.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="p-4 text-center text-gray-500">
                    No complaints found
                  </td>
                </tr>
              )}

              {!loading && visibleComplaints.map((complaint) => {
                const isServiceRecord = complaint.recordType === "service";
                const linkedReport =
                  !isServiceRecord
                    ? reportByComplaintId[String(complaint._id)] ||
                      reportByComplaintId[String(complaint.complaintId || "").trim()] ||
                      reportByComplaintId[String(complaint.primaryId || "").trim()]
                    : null;
                const linkedTravel =
                  !isServiceRecord
                    ? travelByComplaintId[String(complaint._id)] ||
                      travelByComplaintId[String(complaint.complaintId || "").trim()] ||
                      travelByComplaintId[String(complaint.primaryId || "").trim()]
                    : null;
                const travelStarted = Boolean(
                  !isServiceRecord &&
                  linkedTravel &&
                  (
                    linkedTravel.status !== "Not Started" ||
                    linkedTravel.journey?.startTime
                  )
                );
                const canVerify = Boolean(linkedReport?._id);
                const approvalStatus = normalizeApprovalStatus(linkedReport?.approvalStatus);
                const normalizedStatus = isServiceRecord
                  ? normalizeServiceStatus(complaint.status)
                  : normalizeComplaintStatus(complaint.status);

                return (
                  <tr key={complaint._id} className="border-t align-top">
                    <td className="p-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${
                        isServiceRecord
                          ? "bg-violet-100 text-violet-700"
                          : complaint.isAbbWarranty
                            ? "bg-teal-100 text-teal-800"
                            : "bg-sky-100 text-sky-700"
                      }`}>
                        {isServiceRecord ? "Service" : complaint.isAbbWarranty ? "ABB Warranty" : "Complaint"}
                      </span>
                    </td>
                    <td className="p-3 font-medium">{complaint.primaryId}</td>
                    <td className="p-3 text-sm text-gray-600">{formatComplaintDate(complaint.recordDate)}</td>
                    <td className="p-3">{complaint.customerLabel}</td>
                    <td className="p-3 text-sm leading-6 text-gray-600">{complaint.addressLabel}</td>
                    <td className="p-3">
                      {canManageComplaints && !isServiceRecord ? (
                        <div className="flex flex-col gap-2">
                          <select
                            value={engineerSelection[complaint._id] ?? getComplaintEngineerValue(complaint) ?? ""}
                            onChange={(e) =>
                              setEngineerSelection((current) => ({
                                ...current,
                                [complaint._id]: e.target.value
                              }))
                            }
                            className="w-full min-w-[140px] rounded border border-gray-300 px-2 py-1 text-sm"
                            disabled={travelStarted || assigningId === complaint._id}
                          >
                            <option value="">Unassigned</option>
                            {engineers.map((engineer) => (
                              <option key={engineer._id} value={engineerOptionValue(engineer)}>
                                {engineer.engineerId ? `${engineer.name} (${engineer.engineerId})` : engineer.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => updateEngineer(complaint)}
                            disabled={travelStarted || assigningId === complaint._id}
                            className="w-fit rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            {assigningId === complaint._id ? "Saving..." : "Assign"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-slate-700">
                          {isServiceRecord ? complaint.engineerLabel : formatAssignedEngineersForComplaint(complaint)}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {isServiceRecord ? (
                        <span className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
                          {normalizedStatus}
                        </span>
                      ) : (
                        <select
                          value={normalizedStatus}
                          onChange={(e) => updateStatus(complaint._id, e.target.value)}
                          className="w-full min-w-[120px] rounded border border-gray-300 px-2 py-1 text-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="assigned">Assigned</option>
                          <option value="in progress">In Progress</option>
                          <option value="re-visit">Re-Visit</option>
                          <option value="need to provide quote">Need to provide quote</option>
                          <option value="other">Other</option>
                          <option value="completed">Completed</option>
                        </select>
                      )}
                      {!isServiceRecord && linkedReport && approvalStatus && (
                        <p className={`mt-2 text-xs font-semibold ${
                          approvalStatus === "Approved"
                            ? "text-emerald-700"
                            : approvalStatus === "Changes Requested"
                            ? "text-rose-700"
                            : "text-amber-700"
                        }`}>
                          {approvalStatus}
                        </p>
                      )}
                    </td>
                    <td className="p-3">
                      {isServiceRecord ? (
                        <button
                          type="button"
                          onClick={() => navigate("/general-services")}
                          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
                        >
                          Manage Reports
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => canVerify && navigate(canManageComplaints ? `/reports/${linkedReport._id}` : `/accounts/reports/${linkedReport._id}`)}
                          disabled={!canVerify}
                          className={`rounded-lg px-3 py-2 text-sm font-semibold text-white ${
                            canVerify ? "bg-emerald-600 hover:bg-emerald-700" : "bg-gray-300 cursor-not-allowed"
                          }`}
                        >
                          {canVerify ? "View More" : "No Report"}
                        </button>
                      )}
                    </td>
                    {canManageComplaints && (
                      <td className="p-3">
                        <div className="relative inline-block text-left" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setOpenActionDropdownId(openActionDropdownId === complaint._id ? null : complaint._id)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
                          >
                            <span>Actions</span>
                            <span className="material-symbols-outlined text-sm">arrow_drop_down</span>
                          </button>

                          {openActionDropdownId === complaint._id && (
                            <div className="absolute right-0 mt-1 w-40 rounded-xl bg-white shadow-lg border border-slate-200 py-1 z-30 focus:outline-none divide-y divide-slate-100">
                              <button
                                type="button"
                                onClick={() => {
                                  setOpenActionDropdownId(null);
                                  navigate(isServiceRecord ? `/create-complaint?type=service&serviceId=${complaint._id}` : `/complaints/${complaint._id}/edit`);
                                }}
                                className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-2 transition"
                              >
                                <span className="material-symbols-outlined text-sm text-amber-500">edit</span>
                                {isServiceRecord ? "Edit Service" : "Edit Complaint"}
                              </button>
                              {!isServiceRecord && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenActionDropdownId(null);
                                    handleDeleteComplaint(complaint);
                                  }}
                                  disabled={deletingId === complaint._id}
                                  className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2 transition"
                                >
                                  <span className="material-symbols-outlined text-sm text-rose-600">delete</span>
                                  {deletingId === complaint._id ? "Deleting..." : "Delete Complaint"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );

              })}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && filteredComplaints.length > 20 && (
        <div className="flex justify-center items-center gap-3 pt-2">
          {visibleCount < filteredComplaints.length ? (
            <button
              type="button"
              onClick={() => setVisibleCount((prev) => prev + 20)}
              className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 shadow-2xs transition hover:bg-slate-50 flex items-center gap-2"
            >
              <span>View More ({Math.min(20, filteredComplaints.length - visibleCount)} more)</span>
              <span className="text-xs text-slate-400 font-normal">
                (Showing {Math.min(visibleCount, filteredComplaints.length)} of {filteredComplaints.length})
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setVisibleCount(20)}
              className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 shadow-2xs transition hover:bg-slate-50"
            >
              Show Less (Reset to 20)
            </button>
          )}
        </div>
      )}
    </div>
  );
}


